import React, { useState } from 'react';
import { PlusIcon, MinusIcon, ChevronDownIcon } from 'lucide-react';
const faqs = [{
  question: 'How does Emigo work?',
  answer: "When you tap a feature like Reframe, Interpret, or Create, Emigo analyzes your message differently depending on what you need.\n\n• Reframe helps you rewrite something you've already drafted.\n\n• Interpret helps you understand or respond to someone else's message by analyzing tone, intent, and meaning.\n\n• Create helps you craft something new from scratch, like a post, text, or heartfelt message.\n\nEmigo only processes text when you use a feature, and otherwise stays completely offline."
}, {
  question: 'Does Emigo read my messages?',
  answer: 'Emigo has no access to your chats. It only analyzes the text or screenshots you choose when using a feature like Reframe or Interpret. Everything is processed securely and never tied to your identity.'
}, {
  question: 'How does Emigo handle my personal information?',
  answer: 'Emigo does not require you to create an account or provide any personal information. It has no access to your private messages or conversations. When you use a feature, only the text or screenshots you choose to analyze are securely processed to generate a response. This data is handled confidentially, used only for that request, and not stored by Emigo, sold, or linked to your identity.'
}, {
  question: 'Is my data private and secure?',
  answer: "Yes. Your data is private and secure. Emigo doesn't track, sell, or link anything to you. When you use a feature, your text is processed securely in a controlled cloud environment. Emigo does not store or save any local data on your device."
}, {
  question: 'Can Emigo further personalize my responses?',
  answer: "For now, you can personalize Emigo by using the edit feature: just tell it how you'd like a response changed, and it learns your preferences for that session. In the future, we'll introduce optional personalization that can adapt to your tone and emotional style over time, but only if you choose to turn it on."
}];
const FAQItem = ({
  question,
  answer,
  isLast
}: {
  question: string;
  answer: string;
  isLast: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className={`${!isLast ? 'border-b border-gray-200' : ''}`} data-id="element-40">
      <button className="flex justify-between items-start w-full text-left py-6 group hover:bg-gray-50 px-4 -mx-4 rounded-lg transition-all duration-200" onClick={() => setIsOpen(!isOpen)} data-id="element-41">
        <span className="text-lg font-semibold text-gray-900 pr-8 group-hover:text-[#E87B5C] transition-colors duration-200" data-id="element-42">
          {question}
        </span>
        <div className="flex-shrink-0 mt-1" data-id="element-43">
          {isOpen ? <MinusIcon className="h-5 w-5 text-[#E87B5C]" data-id="element-44" /> : <PlusIcon className="h-5 w-5 text-[#E87B5C] group-hover:scale-110 transition-transform duration-200" data-id="element-45" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-46">
        <div className="pb-6 px-4 -mx-4" data-id="element-47">
          <p className="text-gray-600 leading-relaxed whitespace-pre-line" data-id="element-48">
            {answer}
          </p>
        </div>
      </div>
    </div>;
};
const FAQSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  return <section id="faq" className="py-12 md:py-16 bg-white" data-id="element-49">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-50">
        {/* Collapsible Header */}
        <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center gap-3 group cursor-pointer" data-id="element-51">
          <div className="flex items-center gap-4" data-id="element-52">
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 group-hover:text-[#E87B5C] transition-colors duration-200" style={{
            fontFamily: "'DM Serif Display', serif"
          }} data-id="element-53">
              Frequently Asked Questions
            </h2>
            <div className={`flex-shrink-0 p-2 rounded-full border-2 border-[#E87B5C] bg-[#E87B5C]/10 transition-all duration-300`} data-id="element-54">
              <ChevronDownIcon className={`h-7 w-7 text-[#E87B5C] transition-transform duration-300 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} data-id="element-55" />
            </div>
          </div>
        </button>
        <p className="mt-2 text-lg text-gray-600" data-id="element-56">
          Everything you need to know about Emigo.
        </p>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100 mt-12' : 'max-h-0 opacity-0 mt-0'}`} data-id="element-57">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8" data-id="element-58">
            {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isLast={index === faqs.length - 1} data-id="element-59" />)}
          </div>
        </div>
      </div>
    </section>;
};
export default FAQSection;